class MainSearch extends HTMLElement {
  constructor () {
    super()
    this.productsContainer = this.querySelector('[data-search-products]')
    this.pagesContainer = this.querySelector('[data-search-pages]')
    this.articlesContainer = this.querySelector('[data-search-articles]')
    this.productsNavItem = this.querySelector('[data-nav-products]')
    this.pagesNavItem = this.querySelector('[data-nav-pages]')
    this.articlesNavItem = this.querySelector('[data-nav-articles]')
    this.searchTerm = new URLSearchParams(window.location.search).get('q')
    this.basePath = window.Shopify?.routes?.root || '/'
    this.isLoading = false
    this.thresholdOffset = 200

    this.init()
  }

  init () {
    this.setupLoadMore()
    this.loadPagesAndArticles()
    this.setupNavActiveState()
  }

  setupNavActiveState () {
    this.sections = [
      { id: 'search-products', link: this.querySelector('a[href="#search-products"]') },
      { id: 'search-pages', link: this.querySelector('a[href="#search-pages"]') },
      { id: 'search-articles', link: this.querySelector('a[href="#search-articles"]') },
    ].filter((s) => s.link && document.getElementById(s.id))

    if (this.sections.length === 0) return

    this.updateActiveNav()
    window.addEventListener('scroll', this.updateActiveNav.bind(this), { passive: true })

    for (const section of this.sections) {
      section.link.addEventListener('click', () => {
        this.isScrolling = true
        setTimeout(() => {
          this.setActiveSection(section)
        }, 200)
        clearTimeout(this.scrollTimeout)
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false
        }, 1000)
      })
    }
  }

  setActiveSection (activeSection) {
    for (const section of this.sections) {
      const isActive = section === activeSection
      section.link.classList.toggle('bg-black', isActive)
      section.link.classList.toggle('text-white', isActive)
      section.link.classList.toggle('border-black', isActive)
      section.link.classList.toggle('bg-white', !isActive)
      section.link.classList.toggle('text-black', !isActive)
      section.link.classList.toggle('border-clay', !isActive)

      const spinnerIcon = section.link.querySelector('.tb-icon')
      if (spinnerIcon) {
        spinnerIcon.classList.toggle('text-white', isActive)
        spinnerIcon.classList.toggle('text-black', !isActive)
      }
    }
  }

  updateActiveNav () {
    if (this.isScrolling) return

    let activeSection = null
    let closestDistance = Infinity

    for (const section of this.sections) {
      const el = document.getElementById(section.id)
      if (!el || el.offsetParent === null) continue

      const rect = el.getBoundingClientRect()
      const distanceFromThreshold = rect.top - this.thresholdOffset

      if (distanceFromThreshold <= 0 && Math.abs(distanceFromThreshold) < closestDistance) {
        closestDistance = Math.abs(distanceFromThreshold)
        activeSection = section
      }
    }

    if (!activeSection && this.sections.length > 0) {
      activeSection = this.sections[0]
    }

    this.setActiveSection(activeSection)
  }

  setupLoadMore () {
    if (!this.productsContainer) return

    const loadMoreBtn = this.productsContainer.querySelector('[data-load-more-products]')
    if (!loadMoreBtn) return

    this.grid = this.productsContainer.querySelector('[data-products-grid]')
    this.productsPerPage = parseInt(this.productsContainer.dataset.productsPerPage, 10)
    this.totalProducts = parseInt(this.productsContainer.dataset.totalProducts, 10)
    this.currentPage = parseInt(this.productsContainer.dataset.currentPage, 10)
    this.loadedCount = this.grid.children.length
    this.loadMoreBtn = loadMoreBtn
    this.textEl = loadMoreBtn.querySelector('.main-search__load-more-text')
    this.spinnerEl = loadMoreBtn.querySelector('.main-search__load-more-spinner')

    loadMoreBtn.addEventListener('click', this.loadMoreProducts.bind(this))
  }

  setLoading (loading) {
    this.isLoading = loading
    if (this.loadMoreBtn) this.loadMoreBtn.disabled = loading
    if (this.textEl) this.textEl.classList.toggle('invisible', loading)
    if (this.spinnerEl) {
      this.spinnerEl.classList.toggle('hidden', !loading)
      this.spinnerEl.classList.toggle('flex', loading)
    }
  }

  async loadMoreProducts () {
    if (this.isLoading) return
    this.setLoading(true)

    this.currentPage++
    this.productsContainer.dataset.currentPage = this.currentPage

    const url = `${this.basePath}search?q=${encodeURIComponent(
      this.searchTerm,
    )}&type=product&page=${this.currentPage}&section_id=main-search`

    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error('Failed to fetch')

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const newGrid = doc.querySelector('[data-products-grid]')

      if (newGrid && newGrid.children.length > 0) {
        const fragment = document.createDocumentFragment()
        Array.from(newGrid.children).forEach((child) => {
          fragment.appendChild(child.cloneNode(true))
        })
        this.grid.appendChild(fragment)
        this.loadedCount += newGrid.children.length
      }

      if (this.loadedCount >= this.totalProducts) {
        this.loadMoreBtn.parentElement.remove()
      }
    } catch (error) {
      console.error('Error loading more products:', error)
    } finally {
      this.setLoading(false)
    }
  }

  async loadPagesAndArticles () {
    if (!this.searchTerm) {
      this.setAllNavItemsLoaded()
      return
    }

    const pagesList = this.pagesContainer?.querySelector('[data-pages-list]')
    const articlesList = this.articlesContainer?.querySelector('[data-articles-list]')

    let sectionsChanged = false

    const pagesPromise = this.pagesContainer && pagesList && this.pagesContainer.classList.contains('hidden')
      ? this.fetchTypeResults('page')
      : Promise.resolve(null)

    const articlesPromise = this.articlesContainer && articlesList && this.articlesContainer.classList.contains('hidden')
      ? this.fetchTypeResults('article')
      : Promise.resolve(null)

    const [pages, articles] = await Promise.all([pagesPromise, articlesPromise])

    if (pages !== null) {
      if (pages.length > 0) {
        pagesList.innerHTML = ''
        pages.forEach((page) => pagesList.appendChild(this.renderItem(page, 'page')))
        this.pagesContainer.classList.remove('hidden')
        sectionsChanged = true
      } else if (this.pagesNavItem) {
        this.pagesNavItem.hidden = true
      }
    }

    if (articles !== null) {
      if (articles.length > 0) {
        articlesList.innerHTML = ''
        articles.forEach((article) => articlesList.appendChild(this.renderItem(article, 'article')))
        this.articlesContainer.classList.remove('hidden')
        sectionsChanged = true
      } else if (this.articlesNavItem) {
        this.articlesNavItem.hidden = true
      }
    }

    this.setAllNavItemsLoaded()

    if (sectionsChanged) {
      this.setupNavActiveState()
    }
  }

  setAllNavItemsLoaded () {
    this.setNavItemLoaded(this.productsNavItem)
    this.setNavItemLoaded(this.pagesNavItem)
    this.setNavItemLoaded(this.articlesNavItem)
  }

  setNavItemLoaded (navItem) {
    if (!navItem) return
    navItem.removeAttribute('data-loading')
    const textEl = navItem.querySelector('.main-search__nav-link-text')
    const spinnerEl = navItem.querySelector('.loading-overlay__spinner')
    if (textEl) textEl.classList.remove('opacity-0')
    if (spinnerEl) spinnerEl.classList.add('hidden')
  }

  async fetchTypeResults (type) {
    try {
      const url = `${this.basePath}search/suggest.json?q=${encodeURIComponent(
        this.searchTerm,
      )}&${encodeURIComponent('resources[type]')}=${type}&${encodeURIComponent('resources[limit]')}=10`
      const response = await fetch(url)
      if (!response.ok) return []

      const data = await response.json()
      const resultKey = type === 'page' ? 'pages' : 'articles'
      return data.resources?.results?.[resultKey] || []
    } catch (error) {
      console.error(`Error fetching ${type}:`, error)
      return []
    }
  }

  renderItem (item, type) {
    const li = document.createElement('li')
    li.className = `main-search__${type}-item py-2.5 border-b border-clay last:border-b-0`
    const gapClass = type === 'article' ? 'gap-2' : 'gap-2.5'
    li.innerHTML = `
      <a href="${item.url}" class="main-search__${type}-link flex items-center ${gapClass} text-black hover:underline underline-offset-4">
        <span class="main-search__${type}-title">${item.title}</span>
        <span class="tb-icon w-4 h-4 text-orange shrink-0">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
          </svg>
        </span>
      </a>
    `
    return li
  }
}

customElements.define('main-search', MainSearch)
